import axios from "axios";
import store from "../../store/index";

export default {
  name: "TenantLicenseModel",

  data() {
    return {
      baseUrl: process.env.VUE_APP_Service_URL,
      idToken: store.getters.getIdToken,
      isVisible: false,
      isLoading: false,
      isSaving: false,
      searchQuery: "",
      tenant: null,
      rawResources: [],       // Raw resource objects fetched from API
      resourceTree: [],       // Array of root nodes (each node: { id, name, children[] })
      selectedIds: new Set(), // IDs of checked nodes
      expandedIds: new Set(), // IDs of expanded nodes
    };
  },

  computed: {
    headers() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.idToken}`,
      };
    },

    /** All node IDs in the entire tree (every level) */
    allNodeIds() {
      const ids = [];
      const collect = (nodes) => {
        nodes.forEach((n) => {
          ids.push(n.id);
          if (n.children.length) collect(n.children);
        });
      };
      collect(this.resourceTree);
      return ids;
    },

    isAllSelected() {
      return (
        this.allNodeIds.length > 0 &&
        this.allNodeIds.every((id) => this.selectedIds.has(id))
      );
    },

    isIndeterminate() {
      const sel = this.allNodeIds.filter((id) => this.selectedIds.has(id)).length;
      return sel > 0 && sel < this.allNodeIds.length;
    },

    flatList() {
      const q = this.searchQuery.trim().toLowerCase();

      if (q) {
        // Search mode: flatten everything and filter by name match
        const results = [];
        const searchAll = (nodes, depth) => {
          nodes.forEach((node) => {
            if (
              node.name.toLowerCase().includes(q) ||
              (node.resourceCode && node.resourceCode.toLowerCase().includes(q))
            ) {
              results.push({ ...node, depth });
            }
            if (node.children.length) searchAll(node.children, depth + 1);
          });
        };
        searchAll(this.resourceTree, 0);
        return results;
      }

      // Normal mode: only show children of expanded nodes
      const results = [];
      const flatten = (nodes, depth) => {
        nodes.forEach((node) => {
          results.push({ ...node, depth });
          if (this.expandedIds.has(node.id) && node.children.length) {
            flatten(node.children, depth + 1);
          }
        });
      };
      flatten(this.resourceTree, 0);
      return results;
    },
  },

  methods: {
    /** Entry point — called from tenant.js after GET getbyidtenant */
    async openModal(tenantDetail) {
      this.tenant = tenantDetail;
      this.isVisible = true;
      this.searchQuery = "";
      await this.fetchResources();
    },

    closeModal() {
      this.isVisible = false;
      this.tenant = null;
      this.rawResources = [];
      this.resourceTree = [];
      this.selectedIds = new Set();
      this.expandedIds = new Set();
      this.searchQuery = "";
    },

    async fetchResources() {
      this.isLoading = true;
      try {
        const tenantId = typeof this.tenant === "string"
          ? this.tenant
          : (this.tenant?.id || this.tenant?.tenantId || (Array.isArray(this.tenant) ? this.tenant[0]?.tenantId : ""));
        const response = await axios.get(
          `${this.baseUrl}/admin/tenantresourcelicense/getlicensedresource`,
          { params: { tenantId }, headers: this.headers }
        );

        const raw = response.data.entities || response.data || [];
        this.rawResources = Array.isArray(raw) ? raw : [];

        // Build recursive tree
        this.resourceTree = this.buildTree(this.rawResources);

        // Tick licensed/privileged nodes
        const sel = new Set();
        this.rawResources.forEach((r) => {
          if (
            r.isPrevilaged === true ||
            r.isLicensed === true ||
            r.isPrivileged === true ||
            Boolean(r.isPrevilaged) ||
            Boolean(r.isLicensed)
          ) {
            sel.add(r.id);
          }
        });

        // Ensure parent node IDs are in sel if any of their children are selected
        this.rawResources.forEach((r) => {
          const childIds = this.rawResources.filter((c) => c.parentResourceId === r.id).map((c) => c.id);
          if (childIds.length > 0 && childIds.some((cid) => sel.has(cid))) {
            sel.add(r.id);
          }
        });

        this.selectedIds = sel;
        this.expandedIds = new Set();
      } catch (error) {
        const msg = error.response?.data?.message || error.message || "Failed to load resources";
        alert(`Error: ${msg}`);
        console.error("Error fetching licensed resources:", error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Build a recursive parent-child tree from a flat list using parentResourceId.
     *
     * Root rule: a node is a ROOT only when parentResourceId is null, undefined,
     * or an empty string "". This matches the appresource page which shows exactly
     * the nodes with no parent.
     *
     * Orphan rule: if parentResourceId is non-empty but the parent ID is not
     * present in the dataset (deleted / out-of-scope), the node is SKIPPED —
     * it should NOT become a false root.
     */
    buildTree(items) {
      const nodeMap = {};

      // Step 1: create a lookup of all node objects
      items.forEach((item) => {
        nodeMap[item.id] = {
          id: item.id,
          name: item.resourceName || item.resourceCode || item.id,
          resourceCode: item.resourceCode || "",
          resourceType: item.resourceType || "",
          children: [],
        };
      });

      const existingIds = new Set(Object.keys(nodeMap));
      const roots = [];

      // Step 2: wire each node to its parent OR mark it as a root
      items.forEach((item) => {
        const parentId = item.parentResourceId;

        if (!parentId || String(parentId).trim() === "") {
          // No parent declared → true root
          roots.push(nodeMap[item.id]);
        } else if (existingIds.has(parentId)) {
          // Valid parent in dataset → attach as child
          nodeMap[parentId].children.push(nodeMap[item.id]);
        }
        // else: parentId is non-empty but parent is missing (orphan) → skip
      });

      return roots;
    },

    /** Recursively collect all IDs of node + all descendants */
    getAllDescendantIds(node) {
      const ids = [node.id];
      node.children.forEach((child) => {
        ids.push(...this.getAllDescendantIds(child));
      });
      return ids;
    },

    /** Total count of all descendants (not including self) */
    getDescendantCount(node) {
      return node.children.reduce(
        (sum, child) => sum + 1 + this.getDescendantCount(child),
        0
      );
    },

    // ── Selection ──────────────────────────────────────────────────

    isNodeSelected(node) {
      const ids = this.getAllDescendantIds(node);
      return ids.length > 0 && ids.every((id) => this.selectedIds.has(id));
    },

    isNodeIndeterminate(node) {
      const ids = this.getAllDescendantIds(node);
      const count = ids.filter((id) => this.selectedIds.has(id)).length;
      return count > 0 && count < ids.length;
    },

    /** Toggle a node: if fully selected → deselect all descendants; else select all */
    toggleNode(node) {
      const ids = this.getAllDescendantIds(node);
      const newSet = new Set(this.selectedIds);
      if (this.isNodeSelected(node)) {
        ids.forEach((id) => newSet.delete(id));
      } else {
        ids.forEach((id) => newSet.add(id));
      }
      this.selectedIds = newSet;
    },

    /** Toggle the "All resources" root checkbox */
    toggleAll() {
      if (this.isAllSelected) {
        this.selectedIds = new Set();
      } else {
        this.selectedIds = new Set(this.allNodeIds);
      }
    },

    // ── Expand / Collapse ──────────────────────────────────────────

    isExpanded(nodeId) {
      return this.expandedIds.has(nodeId);
    },

    toggleExpand(nodeId) {
      const s = new Set(this.expandedIds);
      if (s.has(nodeId)) {
        s.delete(nodeId);
      } else {
        s.add(nodeId);
      }
      this.expandedIds = s;
    },

    expandAll() {
      this.expandedIds = new Set(this.allNodeIds);
    },

    collapseAll() {
      this.expandedIds = new Set();
    },

    // ── Save ───────────────────────────────────────────────────────

    async saveChanges() {
      this.isSaving = true;
      try {
        const tenantId = String(
          typeof this.tenant === "string"
            ? this.tenant
            : this.tenant?.id || this.tenant?.tenantId || (Array.isArray(this.tenant) ? this.tenant[0]?.tenantId : "")
        );
        const tenantCode = typeof this.tenant === "object" ? (this.tenant.tenantCode || this.tenant.code || "") : "";

        // Helper to group rawResources by module with non-empty moduleCode fallback
        const moduleGroups = new Map();
        this.rawResources.forEach((r) => {
          const modId = r.moduleId || null;
          const modCode = r.moduleCode || r.code || r.resourceCode || r.moduleName || "DEFAULT";
          const key = modId || modCode;
          if (!moduleGroups.has(key)) {
            moduleGroups.set(key, { moduleId: modId, moduleCode: modCode, items: [] });
          }
          moduleGroups.get(key).items.push(r);
        });

        // 1. Fetch tenant resource license entities
        let resLicenseEntities = [];
        try {
          const resLicenseRes = await axios.get(
            `${this.baseUrl}/admin/tenantresourcelicense/getall?tenantId=${encodeURIComponent(tenantId)}`,
            { headers: this.headers }
          );
          const rawResLic = resLicenseRes.data?.entities || resLicenseRes.data?.data || resLicenseRes.data || [];
          resLicenseEntities = Array.isArray(rawResLic) ? rawResLic : (rawResLic && rawResLic.id ? [rawResLic] : []);
        } catch (e) {
          console.warn("Could not fetch tenant resource licenses, falling back to create:", e);
          resLicenseEntities = [];
        }

        // 2. Prepare TenantResourceLicense updates (PUT if exist, POST if missing)
        let resLicenseUpdates = [];
        if (resLicenseEntities.length > 0) {
          resLicenseUpdates = resLicenseEntities.map((entity) => {
            const resourceMap = new Map();
            const matchingRaw = this.rawResources.filter((r) => {
              if (!r.resourceCode) return false;
              if (entity.moduleId && r.moduleId) {
                return String(r.moduleId) === String(entity.moduleId);
              }
              if (entity.moduleCode && (r.moduleCode || r.resourceCode || r.code)) {
                return String(r.moduleCode || r.resourceCode || r.code || "").toLowerCase() === String(entity.moduleCode).toLowerCase();
              }
              return true;
            });

            matchingRaw.forEach((r) => {
              if (!r.resourceCode) return;
              const childIds = this.rawResources.filter((c) => c.parentResourceId === r.id).map((c) => c.id);
              let isLicensed = false;
              if (childIds.length > 0) {
                isLicensed = this.selectedIds.has(r.id) || childIds.some((cid) => this.selectedIds.has(cid));
              } else {
                isLicensed = this.selectedIds.has(r.id);
              }
              resourceMap.set(r.resourceCode, {
                resourceCode: r.resourceCode,
                isLicensed: isLicensed,
                isEnabled: r.isEnabled !== undefined ? Boolean(r.isEnabled) : true,
              });
            });

            const fallbackModCode = entity.moduleCode || matchingRaw[0]?.moduleCode || matchingRaw[0]?.code || matchingRaw[0]?.resourceCode || "DEFAULT";

            return axios.put(
              `${this.baseUrl}/admin/tenantresourcelicense/update?id=${encodeURIComponent(entity.id)}`,
              {
                tenantId: entity.tenantId || tenantId,
                moduleId: entity.moduleId || null,
                moduleCode: fallbackModCode,
                resources: Array.from(resourceMap.values()),
              },
              { headers: this.headers }
            );
          });
        } else {
          // Create new resource licenses per module group
          resLicenseUpdates = Array.from(moduleGroups.values()).map((group) => {
            const resourceMap = new Map();
            group.items.forEach((r) => {
              if (!r.resourceCode) return;
              const childIds = this.rawResources.filter((c) => c.parentResourceId === r.id).map((c) => c.id);
              let isLicensed = false;
              if (childIds.length > 0) {
                isLicensed = this.selectedIds.has(r.id) || childIds.some((cid) => this.selectedIds.has(cid));
              } else {
                isLicensed = this.selectedIds.has(r.id);
              }
              resourceMap.set(r.resourceCode, {
                resourceCode: r.resourceCode,
                isLicensed: isLicensed,
                isEnabled: r.isEnabled !== undefined ? Boolean(r.isEnabled) : true,
              });
            });

            return axios.post(
              `${this.baseUrl}/admin/tenantresourcelicense/create`,
              {
                tenantId: tenantId,
                moduleId: group.moduleId || null,
                moduleCode: group.moduleCode || "DEFAULT",
                resources: Array.from(resourceMap.values()),
              },
              { headers: this.headers }
            );
          });
        }

        // 3. Fetch and update / create /v1/admin/tenantlicense
        let tenantLicenseUpdates = [];
        let tenantLicEntities = [];
        try {
          const tenantLicRes = await axios.get(
            `${this.baseUrl}/admin/tenantlicense/getall?tenantId=${encodeURIComponent(tenantId)}`,
            { headers: this.headers }
          );
          const rawLicData = tenantLicRes.data?.entities || tenantLicRes.data?.data || tenantLicRes.data || [];
          tenantLicEntities = Array.isArray(rawLicData)
            ? rawLicData
            : (rawLicData && typeof rawLicData === 'object' && rawLicData.id ? [rawLicData] : []);
        } catch (licErr) {
          console.warn("Could not fetch tenant license:", licErr);
          tenantLicEntities = [];
        }

        const startDate = new Date();
        const endDate = new Date();
        endDate.setFullYear(startDate.getFullYear() + 1);

        if (tenantLicEntities.length > 0) {
          tenantLicenseUpdates = tenantLicEntities.map((entity) => {
            const existingModules = Array.isArray(entity.modules) ? entity.modules : [];
            const updatedModules = existingModules.map((mod) => {
              const modId = mod.moduleId;
              const modCode = mod.moduleCode || mod.code;
              const matchingRaw = this.rawResources.filter((r) => {
                if (modId && r.moduleId) {
                  return String(r.moduleId) === String(modId);
                }
                if (modCode && (r.moduleCode || r.resourceCode || r.code)) {
                  return String(r.moduleCode || r.resourceCode || r.code || "").toLowerCase() === String(modCode).toLowerCase();
                }
                return false;
              });
              let isLicensed = mod.isLicensed;
              if (matchingRaw.length > 0) {
                isLicensed = matchingRaw.some((r) => {
                  const childIds = this.rawResources.filter((c) => c.parentResourceId === r.id).map((c) => c.id);
                  return this.selectedIds.has(r.id) || childIds.some((cid) => this.selectedIds.has(cid));
                });
              }
              return {
                moduleId: mod.moduleId || null,
                moduleCode: mod.moduleCode || mod.code || "DEFAULT",
                isLicensed: Boolean(isLicensed),
                isEnabled: mod.isEnabled !== undefined ? Boolean(mod.isEnabled) : true,
              };
            });

            const updatePayload = {
              tenantId: entity.tenantId || tenantId,
              tenantCode: entity.tenantCode || tenantCode,
              licenseSchemeCode: entity.licenseSchemeCode || "STANDARD",
              licenseStartDate: entity.licenseStartDate || startDate.toISOString(),
              licenseEndDate: entity.licenseEndDate || endDate.toISOString(),
              modules: updatedModules,
            };

            return axios.put(
              `${this.baseUrl}/admin/tenantlicense/update?id=${encodeURIComponent(entity.id)}`,
              updatePayload,
              { headers: this.headers }
            );
          });
        } else {
          // Create new tenant license
          const updatedModules = Array.from(moduleGroups.values()).map((group) => {
            const isLicensed = group.items.some((r) => {
              const childIds = this.rawResources.filter((c) => c.parentResourceId === r.id).map((c) => c.id);
              return this.selectedIds.has(r.id) || childIds.some((cid) => this.selectedIds.has(cid));
            });
            return {
              moduleId: group.moduleId || null,
              moduleCode: group.moduleCode || "DEFAULT",
              isLicensed: isLicensed,
              isEnabled: true,
            };
          });

          tenantLicenseUpdates = [
            axios.post(
              `${this.baseUrl}/admin/tenantlicense/create`,
              {
                tenantId: tenantId,
                tenantCode: tenantCode,
                licenseSchemeCode: "STANDARD",
                licenseStartDate: startDate.toISOString(),
                licenseEndDate: endDate.toISOString(),
                modules: updatedModules,
              },
              { headers: this.headers }
            )
          ];
        }

        await Promise.all([...resLicenseUpdates, ...tenantLicenseUpdates]);

        const isUpdate = resLicenseEntities.length > 0 || tenantLicEntities.length > 0;
        this.$emit("license-saved");
        alert(isUpdate ? "Tenant licenses updated successfully." : "Tenant licenses created successfully.");
        this.closeModal();
      } catch (error) {
        let msg = error.message || "Failed to save license";
        if (error.response?.data) {
          const d = error.response.data;
          if (typeof d === "string") {
            msg = d;
          } else if (d.errors) {
            msg = Object.values(d.errors).flat().join("; ");
          } else if (d.message) {
            msg = d.message;
          } else if (d.title) {
            msg = d.title;
          } else {
            msg = JSON.stringify(d);
          }
        }
        alert(`Error: ${msg}`);
        console.error("Error saving license:", error, error.response?.data);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
